import React, { useRef, useEffect, useLayoutEffect } from 'react'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'

const StyledWrapper = styled.div`
  height: 74px;
  overflow: hidden;

  ${({ logoInHeader }) =>
  logoInHeader &&
  css`
    padding: 0 0 !important;
  `}

  ${({ companyPage }) =>
    companyPage &&
    css`
      margin: 48px 0;
    `}

  ${({ theme }) => theme.media.desktop} {
    ${({ useColor }) =>
      useColor
        ? css`
            left: -20px;
            margin-top: 12px;
            position: relative;
            right: -20px;
            width: calc(100% + 40px);
          `
        : css`
            bottom: 50px;
            left: -20px;
            margin-top: 24px;
            position: absolute;
            right: -20px;
          `}
  }

  :before,
  :after {
    content: '';
    height: 74px;
    left: 0;
    opacity: 0.72;
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 188px;
    z-index: 1;
  }

  :after {
    left: auto;
    right: 0;
    transform: rotate(180deg);
  }

  ${({ theme }) => theme.media.desktop} {
    margin-top: ${({ useColor }) => (useColor ? '12px' : '48px')};
    position: static;
    width 100%;

    ${({ companyPage }) =>
      companyPage &&
      css`
        left: 0;
        margin: 48px auto;
        right: 0;
      `}

    :before,
    :after {
      display: none;
    }
  }
`

const StyledClientList = styled.ul`
  -webkit-overflow-scrolling: touch;
  align-items: center;
  box-sizing: content-box;
  display: flex;
  flex-wrap: no-wrap;
  height: 74px;
  justify-content: space-between;
  margin: 0 auto;
  overflow-x: scroll;
  scroll-behavior: smooth;

  ::-webkit-scrollbar {
    display: none;
  }

  ${({ mobileStack }) =>
    mobileStack &&
    css`
      display: block;
    `}

  ${({ theme }) => theme.media.desktop} {
    display: flex;
    flex-wrap: no-wrap;
    overflow-x: initial;
    max-width: 100%;
  }
`

const StyledClient = styled.li`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  padding: 0 18px;

  a {
    opacity: ${({ useColor }) => (useColor ? '1' : '0.6')};
    transition: opacity 0.3s ease;

    :hover {
      opacity: 1;
    }

    > img {
      max-width: 200px;
    }
  }

  ${({ mobileStack }) =>
    mobileStack &&
    css`
      :nth-child(2) {
        position: absolute;
      }
    `}

  ${({ darker }) =>
    darker &&
    css`
      a {
        filter: brightness(0.8);
        opacity: 1;
      }
    `}
    
  ${({ theme }) => theme.media.tablet} {
    padding: 0 0;
  }

  ${({ theme }) => theme.media.desktop} {
    display: block;
    margin-bottom: 0px;
    padding: 0 55px;
    width: initial;

    ${({ mobileStack }) =>
      mobileStack &&
      css`
        :nth-child(2) {
          position: relative;
        }
      `}

    ${({ companyPage }) =>
      companyPage &&
      css`
        background-color: #fff;
        z-index: 1000;
      `}
  }
`

const ClientList = ({ clients, cmsData, companyPage, mobileStack, useColor }) => {
  const wrapper = useRef(null)
  const list = useRef(null)

  const darker = cmsData ? true : false

  useLayoutEffect(() => {
    list.current.scroll({ top: 0, left: 20, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  function handleScroll() {
    wrapper.current.getBoundingClientRect().y < 600 &&
      list.current.scroll({
        top: 0,
        left: -(wrapper.current.getBoundingClientRect().y * 0.2 - 100),
        behavior: 'smooth',
      })
  }

  const standarizedData = []
  if (cmsData) {
    cmsData.items.map(item => {
      standarizedData.push(item.fields)
      return null
    })
  }

  const Data = cmsData ? standarizedData : clients

  return (
    <StyledWrapper 
      cmsData={cmsData} 
      companyPage={cmsData ? cmsData.companyPage : companyPage} 
      ref={wrapper} {...useColor}
    >
      <StyledClientList
        ref={list}
        {...useColor}
        mobileStack={cmsData ? cmsData.mobileStack : mobileStack}
      >
        {Data.map((client, index) => (
          <StyledClient
            companyPage={cmsData ? cmsData.companyPage : companyPage}
            mobileStack={cmsData ? cmsData.mobileStack : mobileStack}
            key={index + 99}
            darker={darker}
            {...useColor}
          >
            <a href={client.url} target="_blank" rel="noopener noreferrer">
              <img
                alt={client.name}
                loading="lazy"
                src={
                  cmsData
                    ? cmsData.color
                      ? client.iconColor.fields.file.url
                      : client.iconBw.fields.file.url
                    : useColor
                    ? client.iconColor.file.url
                    : client.iconBw.file.url
                }
              />
            </a>
          </StyledClient>
        ))}
      </StyledClientList>
    </StyledWrapper>
  )
}

ClientList.propTypes = {
  clients: PropTypes.array,
  cmsData: PropTypes.object,
  color: PropTypes.bool,
}

export default ClientList
